import { c as _c } from "react/compiler-runtime";
import { createImageUrlBuilder } from "@sanity/image-url";
import { client } from "@/sanity/client";
import Image from "next/image";
import Link from "next/link";
import { formatPhone, phoneHref } from '@/lib/phoneUtils';
import { isRealogyConfigured, getRealogySupabase } from '@/lib/realogySupabase';
import { normalizePhotoUrl, parseRemarks } from '@/lib/realogyHelpers';
const {
  projectId,
  dataset
} = client.config();
export const urlFor = (source: any) => projectId && dataset ? createImageUrlBuilder({
  projectId,
  dataset
}).image(source) : null;

// Page content interface for CMS-driven page settings
export interface PageContent {
  _id: string;
  heroTitle?: string;
  heroDescription?: string;
  heroImage?: any;
  logo?: any;
  skiTownCard?: {
    title?: string;
    description?: string;
    image?: any;
    icon?: string;
  };
  marketLeadersCard?: {
    title?: string;
    description?: string;
    image?: any;
    icon?: string;
  };
  featuredSectionTitle?: string;
  ctaTitle?: string;
  ctaDescription?: string;
  ctaButtonText?: string;
  ctaButtonAction?: 'link' | 'contact_modal';
  ctaButtonLink?: string;
}
export interface AgentData {
  agentStaffId: string;
  firstName: string;
  lastName: string;
  photoUrl: string | null;
  bio: string;
}
export interface Partner {
  _id: string;
  partnerType: 'ski_town' | 'market_leader';
  firstName: string;
  lastName: string;
  agentStaffId: string;
  slug?: {
    current: string;
  };
  title?: string;
  company?: string;
  location?: string;
  latitude?: number;
  longitude?: number;
  email?: string;
  phone?: string;
  website?: string;
  overridePhoto?: any;
  overrideBio?: string;
  specialties?: string[];
  featured?: boolean;
}
export interface EnrichedPartner extends Partner {
  photoUrl: string | null;
  bio: string;
}
export async function fetchAgentData(agentStaffId: string, firstName?: string, lastName?: string): Promise<AgentData | null> {
  // Query Realogy DB directly (avoids 401 during static build)
  if (!isRealogyConfigured()) return null;
  try {
    const supabase = getRealogySupabase();
    if (!supabase) return null;
    const isUuid = /^[0-9a-f]{8}-/.test(agentStaffId);

    // Best approach: search by name first (most reliable), fall back to ID
    if (firstName && lastName) {
      const firstNameBase = firstName.split(' ')[0]; // "K. Ann" -> "K."
      const {
        data: byNameResults
      } = await supabase.from('realogy_agents').select('first_name, last_name, photo_url, rfg_staff_id, entity_id, id, remarks, office_name').ilike('first_name', `${firstNameBase}%`).ilike('last_name', `${lastName}%`).limit(5);
      if (byNameResults && byNameResults.length > 0) {
        // Prefer exact last name match with a photo
        const best = byNameResults.find(r => r.last_name?.toLowerCase() === lastName.toLowerCase() && r.photo_url) || byNameResults.find(r => r.last_name?.toLowerCase() === lastName.toLowerCase()) || byNameResults.find(r => r.photo_url) || byNameResults[0];
        return {
          agentStaffId: best.rfg_staff_id || best.entity_id || best.id,
          firstName: best.first_name || '',
          lastName: best.last_name || '',
          photoUrl: normalizePhotoUrl(best.photo_url),
          bio: parseRemarks(best.remarks)
        };
      }
    }

    // Fallback: search by ID (for backwards compat)
    const column = isUuid ? 'id' : 'rfg_staff_id';
    let {
      data
    } = await supabase.from('realogy_agents').select('first_name, last_name, photo_url, rfg_staff_id, entity_id, id, remarks, office_name').eq(column, agentStaffId).limit(1).maybeSingle();
    if (!data && !isUuid) {
      const fallback = await supabase.from('realogy_agents').select('first_name, last_name, photo_url, rfg_staff_id, entity_id, id, remarks, office_name').eq('entity_id', agentStaffId).limit(1).maybeSingle();
      data = fallback.data;
    }
    if (!data) return null;
    return {
      agentStaffId: data.rfg_staff_id || data.entity_id || data.id,
      firstName: data.first_name || '',
      lastName: data.last_name || '',
      photoUrl: normalizePhotoUrl(data.photo_url),
      bio: parseRemarks(data.remarks)
    };
  } catch (error) {
    console.error(`Error fetching agent ${agentStaffId}:`, error);
    return null;
  }
}

// Look up an affiliated partner by full agent name (e.g. "Dusty Baker") for
// listing detail pages where the listing has agent_name from Realogy data.
export async function findPartnerByAgentName(agentName: string): Promise<Partner | null> {
  if (!agentName || agentName.trim().length < 2) return null;
  const parts = agentName.trim().split(/\s+/);
  if (parts.length < 2) return null;
  const firstName = parts[0];
  const lastName = parts[parts.length - 1];
  const partner = await client.fetch<Partner | null>(`*[_type == "affiliatedPartner" && active == true && firstName match $first && lastName match $last][0]{
      _id, partnerType, firstName, lastName, agentStaffId, slug, title, company,
      location, email, phone, website, overridePhoto, specialties, featured
    }`, {
    first: `${firstName}*`,
    last: `${lastName}*`
  }, {
    next: {
      revalidate: 300
    }
  });
  return partner;
}
export async function enrichPartnerWithAgentData(partner: Partner): Promise<EnrichedPartner> {
  const agentData = await fetchAgentData(partner.agentStaffId, partner.firstName, partner.lastName);

  // Use override values from Sanity if provided, otherwise use database values
  const photoUrl = partner.overridePhoto ? urlFor(partner.overridePhoto)?.width(400).height(600).url() || null : agentData?.photoUrl || null;
  const bio = partner.overrideBio || agentData?.bio || '';
  return {
    ...partner,
    photoUrl,
    bio
  };
}

// Helper to get partner detail URL
export function getPartnerUrl(partner: Partner | EnrichedPartner): string {
  const nameSlug = partner.slug?.current || `${partner.firstName}-${partner.lastName}`.toLowerCase().replace(/\s+/g, '-');
  return `/real-estate-agent/${nameSlug}`;
}

// Partner Card Component
export function PartnerCard(t0) {
  const $ = _c(53);
  const {
    partner,
    featured: t1
  } = t0;
  const featured = t1 === undefined ? false : t1;
  const cardClasses = featured ? "bg-white dark:bg-[#1a1a1a] border border-[var(--color-gold)]/30 p-8 md:p-10" : "bg-white dark:bg-[#1a1a1a] border border-[#e8e6e3] dark:border-gray-800 p-6 md:p-8 hover:border-[var(--color-gold)]/30 transition-colors duration-300";
  let t2;
  if ($[0] !== partner) {
    t2 = getPartnerUrl(partner);
    $[0] = partner;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const partnerUrl = t2;
  let t3;
  if ($[2] !== partner.firstName || $[3] !== partner.lastName || $[4] !== partner.photoUrl) {
    t3 = partner.photoUrl ? <Image src={partner.photoUrl} alt={`${partner.firstName} ${partner.lastName}`} fill={true} className="object-cover object-top" /> : <div className="w-full h-full flex items-center justify-center text-[#aaa] dark:text-gray-600"><svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" /></svg></div>;
    $[2] = partner.firstName;
    $[3] = partner.lastName;
    $[4] = partner.photoUrl;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== partnerUrl || $[7] !== t3) {
    t4 = <Link href={partnerUrl} className="relative w-full aspect-[4/6] overflow-hidden mb-6 bg-[#f0f0f0] dark:bg-gray-800 block hover:opacity-90 transition-opacity">{t3}</Link>;
    $[6] = partnerUrl;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = `font-serif font-light text-[#1a1a1a] dark:text-white tracking-wide mb-1 ${featured ? "text-2xl" : "text-xl"}`;
  let t6;
  if ($[9] !== partner.firstName) {
    t6 = <span className="block">{partner.firstName}</span>;
    $[9] = partner.firstName;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== partner.lastName) {
    t7 = <span className="block">{partner.lastName}</span>;
    $[11] = partner.lastName;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t5 || $[14] !== t6 || $[15] !== t7) {
    t8 = <h3 className={t5}>{t6}{t7}</h3>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== partnerUrl || $[18] !== t8) {
    t9 = <Link href={partnerUrl} className="hover:text-[var(--color-gold)] transition-colors">{t8}</Link>;
    $[17] = partnerUrl;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== partner.title) {
    t10 = partner.title && <p className="text-[var(--color-gold)] text-sm font-light mb-2">{partner.title}</p>;
    $[20] = partner.title;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== partner.company) {
    t11 = partner.company && <p className="text-[#6a6a6a] dark:text-gray-400 text-sm font-light mb-1">{partner.company}</p>;
    $[22] = partner.company;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== partner.location) {
    t12 = partner.location && <p className="text-[#888] dark:text-gray-500 text-sm font-light mb-4">{partner.location}</p>;
    $[24] = partner.location;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== featured || $[27] !== partner.bio) {
    t13 = partner.bio && <p className={`text-[#4a4a4a] dark:text-gray-300 font-light leading-relaxed mb-6 ${featured ? "text-base" : "text-sm line-clamp-4"}`}>{partner.bio}</p>;
    $[26] = featured;
    $[27] = partner.bio;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== partner.specialties) {
    t14 = partner.specialties && partner.specialties.length > 0 && <div className="flex flex-wrap justify-center gap-2 mb-6">{partner.specialties.map(_temp)}</div>;
    $[29] = partner.specialties;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== partner.email) {
    t15 = partner.email && <a href={`mailto:${partner.email}`} className="text-[#6a6a6a] dark:text-gray-400 hover:text-[var(--color-gold)] transition-colors" title="Email"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg></a>;
    $[31] = partner.email;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== partner.phone) {
    t16 = partner.phone && <a href={`tel:${phoneHref(partner.phone)}`} className="text-[#6a6a6a] dark:text-gray-400 hover:text-[var(--color-gold)] transition-colors" title="Phone"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg></a>;
    $[33] = partner.phone;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== partner.website) {
    t17 = partner.website && <a href={partner.website} target="_blank" rel="noopener noreferrer" className="text-[#6a6a6a] dark:text-gray-400 hover:text-[var(--color-gold)] transition-colors" title="Website"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg></a>;
    $[35] = partner.website;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t15 || $[38] !== t16 || $[39] !== t17) {
    t18 = <div className="flex items-center gap-4">{t15}{t16}{t17}</div>;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t10 || $[42] !== t11 || $[43] !== t12 || $[44] !== t13 || $[45] !== t14 || $[46] !== t18 || $[47] !== t4 || $[48] !== t9) {
    t19 = <div className="flex flex-col items-center text-center">{t4}{t9}{t10}{t11}{t12}{t13}{t14}{t18}</div>;
    $[41] = t10;
    $[42] = t11;
    $[43] = t12;
    $[44] = t13;
    $[45] = t14;
    $[46] = t18;
    $[47] = t4;
    $[48] = t9;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== cardClasses || $[51] !== t19) {
    t20 = <div className={cardClasses}>{t19}</div>;
    $[50] = cardClasses;
    $[51] = t19;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  return t20;
}
function _temp(specialty, index) {
  return <span key={index} className="px-3 py-1 bg-[#f8f7f5] dark:bg-[#252525] text-[#6a6a6a] dark:text-gray-400 text-xs font-light">{specialty}</span>;
}